import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, Grid, CircularProgress, Typography, Chip,
  Card, CardContent, Button, Tooltip, IconButton,
  TextField, InputAdornment, Skeleton, Tabs, Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  AccessTime as ClockIcon,
  KeyboardArrowDown as ArrowDownIcon,
  PersonOutline as PersonIcon,
  BusinessOutlined as OrgIcon,
  FlagOutlined as FlagIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  ViewKanban as KanbanIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';

import TicketDetailModal  from '../../components/kanban/TicketDetailModal';
import ConfirmationDialog from '../../components/kanban/ConfirmationDialog';
import CustomizedSnackbars from '../../components/material-snackbars';
import KanbanFinishModal from './KanbanFinishModal';
import KanbanFillFieldsModal from './KanbanFillFieldsModal';
import TreatCalledModal from '../called-list/components/treat-called-modal';

import Api from '../../services/api';
import { getBoardById, moveCalledStatus, validateRequiredCustomFields } from '../../services/kanban.service';
import { getCalledGridById, updateCalledBasicData } from '../../services/called.service';
import { getByOrganizationIdDapper } from '../../services/organizationsetting.service';
import * as calledsActions from '../../redux/actions/calleds';
import { useAgentWorkspaceHubContext } from '../../contexts/AgentWorkspaceHubContext';
import { useIntl } from 'react-intl';
import { buildCalledFinishFormData } from './kanbanFinish.helpers';

// Maps tb_automation_condition_criteria.id to /calleds/paginate field names
const CRITERIA_ID_MAP = {
  2:  'title',
  5:  'type',
  7:  'status',
  8:  'origin',
  9:  'loc',
  10: 'org',
  11: 'categoryId',
  12: 'assigneduser',
};

const buildPaginateParams = (filters) => {
  const fields   = [];
  const searches = [];

  (filters ?? []).forEach((filterGroup) => {
    (filterGroup.conditions ?? []).forEach((cond) => {
      const mappedField = CRITERIA_ID_MAP[cond.criteriaId];
      if (!mappedField) return;
      const values = cond.values ?? [];
      if (values.length === 0) return;
      values.forEach((v) => {
        String(v).split(',').forEach((sv) => {
          const trimmed = sv.trim();
          if (trimmed) {
            fields.push(mappedField);
            searches.push(trimmed);
          }
        });
      });
    });
  });

  return { fields, searches };
};

const buildServerSearchParams = (term) => {
  const trimmed = (term ?? '').trim();
  if (!trimmed) return { fields: [], searches: [] };
  const isNum = /^\d+$/.test(trimmed);
  if (isNum) return { fields: ['id', 'title'], searches: [trimmed, trimmed] };
  return { fields: ['title'], searches: [trimmed] };
};

const buildColumnUrl = (col, filterFields, filterSearches, page = 1) => {
  const colStatuses = col.statuses ?? [];
  const colStatusIds = colStatuses.map((s) => s.statusId);
  if (colStatusIds.length === 0) return null;

  const typeIds = colStatuses.map((s) => s.statusTypeId);
  const isFinished = typeIds.some((t) => t === 5) ? 1 : 0;
  const isCanceled = !isFinished && typeIds.some((t) => t === 4) ? 1 : 0;
  

  const fields   = [];
  const searches = [];

  colStatusIds.forEach((id) => {
    fields.push('statusid');
    searches.push(String(id));
  });

  fields.push(...filterFields);
  searches.push(...filterSearches);

  return (
    `/calleds/paginate?page=${page}&type=0&isarchived=0&isfinished=${isFinished}&iscanceled=${isCanceled}&ofield=open&osearch=2` +
    (fields.length > 0 ? `&field=${fields.join('|')}&search=${searches.join('|')}` : '')
  );
};

const normalizeStatus = (st) => ({
  id:           st.id,
  statusId:     st.statusid,
  statusName:   st.statusname,
  colorHex:     st.statuscolorhex ?? st.colorhex ?? '#6b7280',
  sortOrder:    st.sortorder,
  statusTypeId: st.statustypeid ?? null,
});

const normalizeColumn = (col) => ({
  id:        col.id,
  name:      col.name,
  sortOrder: col.sortorder,
  colorHex:  col.colorhex ?? '#6b7280',
  statuses:  (col.statuses ?? []).map(normalizeStatus),
});

const normalizeCondition = (cond) => ({
  id:              cond.id,
  criteriaId:      cond.automationconditioncriteriaid,
  criteriaName:    cond.criterianame,
  operatorName:    cond.operatorname,
  logicalOperator: cond.logicaloperator,
  values:          cond.values ?? [],
});

const normalizeFilter = (f) => ({
  id:              f.id,
  logicalOperator: f.logicaloperator,
  sortOrder:       f.sortorder,
  conditions:      (f.conditions ?? []).map(normalizeCondition),
});

const normalizeBoard = (b) => ({
  id:                    b.id,
  title:                 b.title,
  active:                b.active,
  isDefault:             b.isdefault,
  groupBy:               b.groupby   ?? 'NONE',
  filterLogicalOperator: b.filterlogicaloperator ?? 'AND',
  columns:               (b.columns ?? []).map(normalizeColumn),
  filters:               (b.filters  ?? []).map(normalizeFilter),
});

const formatCalledDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh   = String(d.getHours()).padStart(2, '0');
  const min  = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const normalizeCalledItem = (item) => ({
  id:                     item.id,
  title:                  item.title ?? '',
  statusId:               item.status?.id ?? item.statusid ?? null,
  statusName:             item.status?.name ?? '',
  statusColorHex:         item.status?.colorhex ?? '#6b7280',
  priorityName:           item.priority?.name ?? '',
  priorityColorHex:       item.priority?.colorhex ?? '#6b7280',
  organizationName:       item.organization?.name ?? '',
  organizationId:         item.organization?.id ?? item.organizationid ?? null,
  categoryName:           item.organizationcategory?.parentname
                            ? item.organizationcategory.parentname
                            : (item.organizationcategory?.name ?? ''),
  assignedUserName:       item.assigneduser?.name ?? '',
  requestingUserName:     item.requestinguser?.name ?? '',
  assignedUserId:         item.assigneduser?.id ?? item.assigneduserid ?? null,
  organizationCategoryId: item.organizationcategory?.id ?? item.organizationcategoryid ?? null,
  openDate:               formatCalledDate(item.openingdate ?? ''),
  dueDate:                formatCalledDate(item.duedate ?? item.duedatetopresent ?? ''),
});

// ============================================================
// SignalR helpers — converte WorkItem (AgentWorkspaceHub) p/ Called Kanban
// ============================================================

// SignalR ItemType: 0=Called, 1=Chat. Hoje o Kanban so trata Called.
const KANBAN_ITEM_TYPE_CALLED = 0;

// Mapeia WorkItem (vindo no SignalR) p/ shape do Called esperado pelo Kanban.
// WorkItem agora traz statusId (migration 20260615120000) — matching de coluna por ID
// e robusto a renomeacoes de status.
const signalRItemToCalled = (workItem) => {
  if (!workItem) return null;
  return {
    id:                     workItem.itemid ?? workItem.itemId ?? null,
    title:                  workItem.title ?? workItem.displaytitle ?? workItem.displayTitle ?? '',
    statusId:               workItem.statusid ?? workItem.statusId ?? null,
    statusName:             workItem.statusname ?? workItem.statusName ?? '',
    statusColorHex:         workItem.statuscolor ?? workItem.statusColor ?? '#6b7280',
    priorityName:           workItem.priorityname ?? workItem.priorityName ?? '',
    priorityColorHex:       workItem.prioritycolor ?? workItem.priorityColor ?? '#6b7280',
    organizationName:       workItem.organizationname ?? workItem.organizationName ?? '',
    organizationId:         workItem.organizationid ?? workItem.organizationId ?? null,
    categoryName:           workItem.categoryname ?? workItem.categoryName ?? '',
    assignedUserName:       '',
    requestingUserName:     workItem.requestingusername ?? workItem.requestingUserName ?? '',
    assignedUserId:         workItem.assigneduserid ?? workItem.assignedUserId ?? null,
    organizationCategoryId: null,
    openDate:               formatCalledDate(workItem.openingdate ?? workItem.openingDate ?? ''),
    dueDate:                formatCalledDate(workItem.duedate ?? workItem.dueDate ?? ''),
  };
};

// Encontra a coluna do board que aceita um called pelo statusId.
// Fallback opcional por statusName (caso WorkItem antigo sem status_id chegue durante deploy).
const findColumnIdForStatus = (statusId, statusName, columns) => {
  if (!Array.isArray(columns)) return null;
  if (statusId != null) {
    const colById = columns.find((c) =>
      (c.statuses ?? []).some((s) => s.statusId === statusId)
    );
    if (colById) return colById.id;
  }
  if (statusName) {
    const target = String(statusName).toLowerCase().trim();
    const colByName = columns.find((c) =>
      (c.statuses ?? []).some((s) => String(s.statusName ?? '').toLowerCase().trim() === target)
    );
    if (colByName) return colByName.id;
  }
  return null;
};

// Encontra o ID atual da coluna onde o called esta hoje (busca em todo o calledsByColumn).
const findCurrentColumnIdForCalled = (calledId, byCol) => {
  const entry = Object.entries(byCol || {}).find(([, items]) => items.some((c) => c.id === calledId));
  return entry ? entry[0] : null;
};

// Workaround for react-beautiful-dnd not mounting in React 18 StrictMode
const StrictModeDroppable = forwardRef((props, ref) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const anim = requestAnimationFrame(() => setEnabled(true));
    return () => { cancelAnimationFrame(anim); setEnabled(false); };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} ref={ref} />;
});
StrictModeDroppable.displayName = 'StrictModeDroppable';

const hexWithAlpha = (hex, alpha) => {
  if (!hex || hex.length < 7) return `rgba(107,114,128,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const GROUP_LABELS = {
  ORGANIZATION:          { key: 'organizationName',  icon: <OrgIcon fontSize="small" />,    label: 'Organização' },
  ORGANIZATION_CATEGORY: { key: 'categoryName',      icon: <OrgIcon fontSize="small" />,    label: 'Categoria' },
  ASSIGNED_USER:         { key: 'assignedUserName',  icon: <PersonIcon fontSize="small" />, label: 'Responsável' },
  PRIORITY:              { key: 'priorityName',      icon: <FlagIcon fontSize="small" />,   label: 'Prioridade' },
};

const groupCalleds = (calleds, groupBy) => {
  if (!groupBy || groupBy === 'NONE') return [{ key: '__all__', label: null, colorHex: null, calleds }];

  const cfg = GROUP_LABELS[groupBy];
  if (!cfg) return [{ key: '__all__', label: null, colorHex: null, calleds }];

  const map = {};
  calleds.forEach((c) => {
    const k = c[cfg.key] || '(Sem ' + cfg.label.toLowerCase() + ')';
    if (!map[k]) map[k] = { key: k, label: k, colorHex: c.priorityColorHex ?? null, calleds: [] };
    map[k].calleds.push(c);
  });
  return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
};

const TicketCard = ({ called, index, onClick, invalidFieldsCalledId }) => {
  const { lang } = useParams();
  const hasRequiredFieldsError = called.id === invalidFieldsCalledId;
  return (
  <Draggable draggableId={String(called.id)} index={index}>
    {(provided, snapshot) => (
      <Card
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        onClick={() => onClick(called)}
        sx={{
          mb: 1.5,
          borderRadius: '10px',
          cursor: 'pointer',
          border: '1px solid #e5e7eb',
          boxShadow: snapshot.isDragging ? '0 10px 20px rgba(0,0,0,0.15)' : 'none',
          transform: snapshot.isDragging
            ? `${provided.draggableProps.style?.transform || ''} rotate(1.5deg)`
            : provided.draggableProps.style?.transform,
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
          ...provided.draggableProps.style,
        }}
        style={{
          ...provided.draggableProps.style,
          outline: hasRequiredFieldsError ? '2px solid #ef4444' : undefined,
          outlineOffset: hasRequiredFieldsError ? '-2px' : undefined,
        }}
      >
        <CardContent sx={{ p: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">#{called.id}</Typography>
            <Chip
              label={called.priorityName}
              size="small"
              sx={{
                bgcolor: hexWithAlpha(called.priorityColorHex || '#6b7280', 0.12),
                color:   called.priorityColorHex || '#6b7280',
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, lineHeight: 1.3 }}>
            <a
              href={`/${lang}/called-accompaniment/${called.id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#1976d2', textDecoration: 'underline' }}
            >
              {called.title}
            </a>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {called.organizationName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <ClockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">{called.dueDate}</Typography>
          </Box>

          {called.assignedUserName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <PersonIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">{called.assignedUserName}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
            <Tooltip title={called.categoryName || ''} placement="top">
              <Chip
                label={called.categoryName}
                size="small"
                sx={{
                  bgcolor: '#f3f4f6',
                  color: '#4b5563',
                  fontSize: '0.65rem',
                  height: 18,
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  },
                }}
              />
            </Tooltip>
            {called.statusName && (
              <Chip
                label={called.statusName}
                size="small"
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  bgcolor: hexWithAlpha(called.statusColorHex || '#6b7280', 0.12),
                  color:   called.statusColorHex || '#6b7280',
                  fontSize: '0.65rem',
                  height: 18,
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    )}
  </Draggable>
  );
};

const GroupSectionHeader = ({ group, isCollapsed, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.5,
      cursor: 'pointer',
      userSelect: 'none',
      bgcolor: '#f3f4f6',
      borderTop: '1px solid',
      borderBottom: '1px solid',
      borderColor: '#e5e7eb',
      '&:hover': { bgcolor: '#e9eaec' },
    }}
  >
    <Typography sx={{ fontSize: 11, color: 'text.disabled', width: 12, lineHeight: 1 }}>
      {isCollapsed ? '▶' : '▼'}
    </Typography>
    <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
      {group.label}
    </Typography>
    <Chip
      label={group.calleds.length}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.6rem',
        bgcolor: '#e5e7eb',
        color: 'text.secondary',
      }}
    />
  </Box>
);

const KanbanColumn = ({
  column, calleds, onCardClick, hasMoreFromServer, loadingMore, totalCount, onLoadMore, droppableId,
  groups, collapsedGroups, onToggleGroup,
  isDragging, isSourceColumn, invalidFieldsCalledId,
}) => {
  const visible = calleds;

  let flatVisibleGrouped = [];
  if (groups) {
    groups.forEach((g) => {
      const isCollapsed = collapsedGroups?.has(`${column.id}__${g.key}`);
      if (!isCollapsed) flatVisibleGrouped.push(...g.calleds);
    });
  }

  // Pre-compute index map for O(1) lookup inside grouped render
  const groupedIndexMap = new Map(flatVisibleGrouped.map((c, i) => [c.id, i]));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#fff', position: 'relative' }}>
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `3px solid ${column.colorHex}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: column.colorHex }} />
          <Typography variant="subtitle2">{column.name}</Typography>
        </Box>
        <Chip label={totalCount ?? calleds.length} size="small" sx={{ bgcolor: '#f3f4f6', minWidth: 28, height: 20 }} />
      </Box>

      <Box sx={{ px: 1.5, pb: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {column.statuses.map((st) => (
          <Chip
            key={st.statusId}
            label={st.statusName}
            size="small"
            sx={{ bgcolor: hexWithAlpha(st.colorHex, 0.1), color: st.colorHex, fontSize: '0.6rem', height: 16 }}
          />
        ))}
      </Box>

      <StrictModeDroppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              p: groups ? 0 : 1.5,
              flexGrow: 1,
              overflowY: 'auto',
              bgcolor: snapshot.isDraggingOver ? '#f3f4f6' : '#fff',
              transition: 'background-color 0.15s ease',
              '&::-webkit-scrollbar':            { width: 5 },
              '&::-webkit-scrollbar-track':      { bgcolor: '#f1f1f1' },
              '&::-webkit-scrollbar-thumb':      { bgcolor: '#d1d5db', borderRadius: 3 },
              '&::-webkit-scrollbar-thumb:hover':{ bgcolor: '#9ca3af' },
            }}
          >
            {groups ? (
              <>
                {isDragging && !isSourceColumn ? (
                  // During drag over destination column: flatten groups to avoid header interference
                  <Box sx={{ p: 1, pb: 0 }}>
                    {flatVisibleGrouped.map((c) => (
                      <TicketCard key={c.id} called={c} index={groupedIndexMap.get(c.id)} onClick={onCardClick} invalidFieldsCalledId={invalidFieldsCalledId} />
                    ))}
                  </Box>
                ) : groups.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Nenhum chamado</Typography>
                  </Box>
                ) : (
                  groups.map((g) => {
                    const isCollapsed = collapsedGroups?.has(`${column.id}__${g.key}`);
                    const groupCardIds = new Set(g.calleds.map((gc) => gc.id));
                    const groupCards   = flatVisibleGrouped.filter((c) => groupCardIds.has(c.id));
                    return (
                      <Box key={g.key}>
                        <GroupSectionHeader
                          group={g}
                          isCollapsed={isCollapsed}
                          onToggle={() => onToggleGroup?.(column.id, g.key)}
                        />
                        {!isCollapsed && (
                          <Box sx={{ p: 1, pb: 0 }}>
                            {groupCards.length === 0 ? (
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                                Não há chamados
                              </Typography>
                            ) : groupCards.map((c) => (
                              <TicketCard
                                key={c.id}
                                called={c}
                                index={groupedIndexMap.get(c.id) ?? 0}
                                onClick={onCardClick}
                                invalidFieldsCalledId={invalidFieldsCalledId}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
                {provided.placeholder}
                {(hasMoreFromServer || loadingMore) && (
                  <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onLoadMore(droppableId)}
                      disabled={loadingMore}
                      startIcon={loadingMore ? <CircularProgress size={14} /> : <ArrowDownIcon />}
                      sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                    >
                      {loadingMore ? 'Carregando…' : 'Carregar mais'}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <>
                {visible.length > 0 ? (
                  visible.map((c, idx) => (
                    <TicketCard key={c.id} called={c} index={idx} onClick={onCardClick} invalidFieldsCalledId={invalidFieldsCalledId} />
                  ))
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">Nenhum chamado</Typography>
                  </Box>
                )}
                {provided.placeholder}
                {(hasMoreFromServer || loadingMore) && (
                  <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onLoadMore(droppableId)}
                      disabled={loadingMore}
                      startIcon={loadingMore ? <CircularProgress size={14} /> : <ArrowDownIcon />}
                      sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                    >
                      {loadingMore ? 'Carregando…' : 'Carregar mais'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </StrictModeDroppable>

      {isSourceColumn && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(239,68,68,0.07)',
            border: '2px dashed #ef4444',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #fca5a5',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              textAlign: 'center',
              boxShadow: 2,
            }}
          >
            <Typography sx={{ fontSize: 20, lineHeight: 1.2 }}>🚫</Typography>
            <Typography variant="caption" color="error.main" display="block">
              Mesma coluna
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Mova para outro status
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const StatusSelectDialog = ({ open, column, onSelect, onCancel }) => {
  if (!open || !column) return null;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1300,
        bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <Box
        sx={{ bgcolor: '#fff', borderRadius: '12px', p: 3, minWidth: 320, maxWidth: 400 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Selecionar status destino
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A coluna {column.name} possui mais de um status. Para qual status deseja mover o chamado?
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {column.statuses.map((st) => (
            <Button
              key={st.statusId}
              variant="outlined"
              fullWidth
              onClick={() => onSelect(st)}
              sx={{
                justifyContent: 'flex-start',
                borderColor: st.colorHex,
                color: st.colorHex,
                '&:hover': { bgcolor: hexWithAlpha(st.colorHex, 0.08) },
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.colorHex, mr: 1 }} />
              {st.statusName}
            </Button>
          ))}
        </Box>
        <Button fullWidth variant="text" onClick={onCancel} sx={{ mt: 1.5, textTransform: 'none', color: 'text.secondary' }}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

const KanbanCardSkeleton = () => (
  <Box sx={{ mb: 1.5, borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff', p: 1.5, border: '1px solid #e5e7eb' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Skeleton variant="text" width={30} height={16} />
      <Skeleton variant="rounded" width={52} height={20} />
    </Box>
    <Skeleton variant="text" width="90%" height={18} />
    <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
      <Skeleton variant="circular" width={13} height={13} />
      <Skeleton variant="text" width={90} height={14} />
    </Box>
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
      <Skeleton variant="rounded" width={72} height={18} />
      <Skeleton variant="rounded" width={52} height={18} sx={{ ml: 'auto' }} />
    </Box>
  </Box>
);

const KanbanColumnSkeleton = () => (
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
    <Box sx={{ p: 1.5, borderTop: '3px solid rgba(0,0,0,0.11)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={100} height={22} />
        </Box>
        <Skeleton variant="rounded" width={28} height={20} />
      </Box>
    </Box>
    <Box sx={{ px: 1.5, pb: 0.75, display: 'flex', gap: 0.5 }}>
      <Skeleton variant="rounded" width={62} height={16} />
      <Skeleton variant="rounded" width={80} height={16} />
    </Box>
    <Box sx={{ p: 1.5, flexGrow: 1, bgcolor: '#fff' }}>
      {[1, 2, 3].map((i) => (
        <KanbanCardSkeleton key={i} />
      ))}
    </Box>
  </Box>
);

export const KanbanSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '10px', flexShrink: 0 }} />
            <Box>
              <Skeleton variant="text" width={170} height={28} />
              <Skeleton variant="text" width={220} height={18} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={220} height={36} />
            <Skeleton variant="rounded" width={130} height={36} />
            <Skeleton variant="rounded" width={90} height={24} />
            <Skeleton variant="rounded" width={36} height={36} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, p: 2, overflowX: 'auto' }}>
        <Grid container spacing={1.5} sx={{ height: 'calc(100vh - 220px)' }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item key={i} xs={12} md={3} sx={{ height: '100%' }}>
              <KanbanColumnSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
);

const TicketKanban = ({ boardId, sidebarOpen, onToggleSidebar, userSession }) => {
  const history    = useHistory();
  const { lang }   = useParams();
  const dispatch   = useDispatch();
  const intl       = useIntl();

  const [board,            setBoard]            = useState(null);
  const [calledsByColumn,  setCalledsByColumn]  = useState({});
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [columnMeta,       setColumnMeta]       = useState({});
  const [searchTerm,       setSearchTerm]       = useState('');
  const [searchResults,  setSearchResults]  = useState(null);
  const [searchLoading,  setSearchLoading]  = useState(false);

  const [selectedCalled,   setSelectedCalled]   = useState(null);
  const [modalOpen,        setModalOpen]         = useState(false);
  const [confirmOpen,      setConfirmOpen]       = useState(false);
  const [statusSelectOpen, setStatusSelectOpen]  = useState(false);
  const [pendingMove,      setPendingMove]       = useState(null);
  const [movingCalled,     setMovingCalled]      = useState(false);
  const [fillFieldsModal,  setFillFieldsModal]   = useState({ open: false, calledId: null, errorMessage: '' });
  const [finishModal, setFinishModal] = useState({
    open: false, calledId: null, targetStatusId: null, targetStatus: null,
  });
  const [treatModal, setTreatModal] = useState({ open: false, calledId: null, targetStatusId: null });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [isDragging,    setIsDragging]    = useState(false);
  const [draggingColId, setDraggingColId] = useState(null);
  const [invalidFieldsCalledId, setInvalidFieldsCalledId] = useState(null);

  // Padrao B: toggle "Minha visao" (default) vs "Visao geral" (supervisor opt-in).
  // - 'mine': filtra chamados atribuidos ao agente logado (assigneduserid={userId})
  // - 'all':  remove filtro de agente; backend filtra por role (CalledController:538-863):
  //           Adm Sistema -> ve tudo; Adm Org/Coord -> apenas chamados das SUAS orgs.
  const [viewMode, setViewMode] = useState('mine');

  // Role detection — fielservice.tb_roles.role_type:
  //   0 Administrador Sistema (Adm Sistema)
  //   1 Administrador de Organizacoes (Adm Org)
  //   2 Tecnico
  //   3 Cliente
  //   4 Coordenador de Organizacoes (Coord Org)
  const userRoles = userSession?.user?.userrole ?? [];
  const isAdmSistema = userRoles.some((ur) => ur?.role?.roletype === 0);
  const isAdmOrg     = userRoles.some((ur) => ur?.role?.roletype === 1);
  const isCoordOrg   = userRoles.some((ur) => ur?.role?.roletype === 4);
  const isClient     = userRoles.length > 0 && userRoles.every((ur) => ur?.role?.roletype === 3);
  // Quem pode ativar "Visao geral" (Padrao B). Tecnicos so veem 'mine' (sem toggle).
  const isSupervisor = isAdmSistema || isAdmOrg || isCoordOrg;
  const currentUserId = userSession?.user?.id ?? 0;
  const hirerId = userSession?.user?.hirerid ?? 0;

  // Orgs do user — usadas pelo Adm Org / Coord pra fazer N JoinSupervisorGroup(hirerId, orgId).
  // Adm Sistema nao usa (ele faz 1 join sem orgId pra entrar no grupo "_all").
  const supervisorOrgIds = React.useMemo(() => {
    if (isAdmSistema) return []; // Adm Sistema usa _all, nao precisa de lista de orgs
    if (!isSupervisor) return [];
    const orgs = userSession?.user?.userorganizations ?? [];
    return orgs.map((o) => o?.id).filter((id) => typeof id === 'number' && id > 0);
  }, [isAdmSistema, isSupervisor, userSession?.user?.userorganizations]);

  // Tracks optimistic moves pending background API resolution: calledId → { originalColId, originalCalled, targetStatusId }
  const optimisticMovesRef = React.useRef({});
  const timerRef = useRef(null);
  const clientFinishInFlightRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setSearchResults(null);
      setLoading(true);
      setError(null);
      try {
        const boardRes = await getBoardById(boardId);
        if (cancelled) return;

        const raw = boardRes.data?.data;
        if (!raw) { setError('Quadro não encontrado.'); setLoading(false); return; }
        const b = normalizeBoard(raw);

        const { fields: filterFields, searches: filterSearches } = buildPaginateParams(b.filters);
        // Busca server-side agora vive na arquitetura searchResults/searchLoading (sobrepoe
        // calledsByColumn quando o usuario busca). O load inicial do board nao precisa mais
        // do termo de busca aqui — quando busca dispara, searchResults vira o estado visivel.
        // Padrao B: em modo 'mine' adicionamos filtro de assigned_user_id. Em modo 'all'
        // o backend retorna o que o usuario tem permissao de ver (admin global ve tudo do hirer).
        const viewFields   = [];
        const viewSearches = [];
        if (!isClient && viewMode === 'mine' && currentUserId > 0) {
          viewFields.push('assigneduserid');
          viewSearches.push(String(currentUserId));
        }
        const allFields   = [...filterFields, ...viewFields];
        const allSearches = [...filterSearches, ...viewSearches];

        const colResults = await Promise.all(
          (b.columns ?? []).map(async (col) => {
            // Usa allFields/allSearches (filtros do board + Padrao B viewMode='mine').
            // Sem isso, o modo 'mine' nao aplicava o filtro assigneduserid={userId}
            // no load inicial — agente via TODOS os chamados independente do viewMode.
            const url = buildColumnUrl(col, allFields, allSearches, 1);
            if (!url) return { colId: col.id, calleds: [], count: 0 };
            const res      = await Api.get(url);
            const rawItems = res.data?.data ?? [];
            return {
              colId:   col.id,
              calleds: rawItems.map(normalizeCalledItem),
              count:   res.data?.count ?? 0,
            };
          })
        );
        if (cancelled) return;

        const byCol = {};
        const meta  = {};
        colResults.forEach(({ colId, calleds: c, count }) => {
          byCol[String(colId)] = c;
          meta[String(colId)]  = { page: 1, count, loadingMore: false };
        });

        setBoard(b);
        setCalledsByColumn(byCol);
        setColumnMeta(meta);
        setLoading(false);
      } catch (err) {
        if (!cancelled) { setError('Erro ao carregar o quadro.'); setLoading(false); }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [boardId, viewMode, currentUserId, isClient]);

  // ============================================================
  // SignalR — tempo real via AgentWorkspaceHub
  // ============================================================
  // O AgentWorkspaceHubContext ja gerencia a conexao (App.js) e auto-join no grupo
  // agent_{hirerId}_{userId}. Aqui apenas registramos callbacks para os eventos
  // que afetam o Kanban: NewItem, ItemUpdated, ItemClosed, ItemRemoved.
  //
  // Padrao A (default): o agente logado ve em tempo real os chamados que
  // foram atribuidos a ele. O backend filtra antes de publicar.
  const hubContext = useAgentWorkspaceHubContext?.();
  const boardRef = useRef(null);
  useEffect(() => { boardRef.current = board; }, [board]);

  useEffect(() => {
    if (!hubContext || !board) return;

    // Helper interno: aplica a chegada/atualizacao de UM called no calledsByColumn.
    // - Se nenhuma coluna do board ativo aceita o status, ignora silenciosamente
    //   (called esta fora do escopo deste board).
    // - Se ja existe no kanban e a coluna nao mudou, faz patch in-place.
    // - Se ja existe e a coluna mudou (status mudou), move pra coluna nova.
    // - Se nao existe, prepend na coluna nova.
    const upsertCalledOnBoard = (called) => {
      if (!called?.id) return;
      const currentBoard = boardRef.current;
      // Matching por statusId (robusto a renames). Fallback por statusName se id ausente.
      const targetColId = findColumnIdForStatus(called.statusId, called.statusName, currentBoard?.columns);
      if (!targetColId) return; // status fora do board ativo

      setCalledsByColumn((prev) => {
        const currentColId = findCurrentColumnIdForCalled(called.id, prev);
        const next = { ...prev };

        // Remover de todas (vai re-inserir abaixo na coluna correta)
        Object.keys(next).forEach((colId) => {
          if (next[colId].some((c) => c.id === called.id)) {
            next[colId] = next[colId].filter((c) => c.id !== called.id);
          }
        });

        // Inserir no destino. Se ja existia, preserva a posicao caso coluna nao mudou;
        // caso contrario prepend (item recem-movido fica no topo).
        const destKey = String(targetColId);
        const existing = (prev[destKey] ?? []).find((c) => c.id === called.id);
        if (existing && String(currentColId) === destKey) {
          // mesma coluna -> preserva ordem (patch in-place)
          next[destKey] = (prev[destKey] ?? []).map((c) => (c.id === called.id ? { ...c, ...called } : c));
        } else {
          // entrou agora ou veio de outra coluna -> prepend
          next[destKey] = [{ ...(existing ?? {}), ...called }, ...(next[destKey] ?? []).filter((c) => c.id !== called.id)];
        }
        return next;
      });
    };

    const removeCalledFromBoard = (calledId) => {
      if (!calledId) return;
      setCalledsByColumn((prev) => {
        const next = {};
        let removed = false;
        Object.entries(prev).forEach(([colId, items]) => {
          const filtered = items.filter((c) => c.id !== calledId);
          if (filtered.length !== items.length) removed = true;
          next[colId] = filtered;
        });
        return removed ? next : prev;
      });
    };

    // -------------------- handlers --------------------

    const onNewItem = (workItem) => {
      // WorkItem.ItemType: 0=Called, 1=Chat. Kanban hoje so opera Called.
      if (!workItem || workItem.itemtype !== KANBAN_ITEM_TYPE_CALLED) return;
      const called = signalRItemToCalled(workItem);
      if (called) upsertCalledOnBoard(called);
    };

    const onItemUpdated = (workItem) => {
      if (!workItem || workItem.itemtype !== KANBAN_ITEM_TYPE_CALLED) return;
      const called = signalRItemToCalled(workItem);
      if (called) upsertCalledOnBoard(called);
    };

    const onItemClosed = (data) => {
      // Payload do ItemClosed e diferente: { itemId, itemType: 'CALLED'|'CHAT' }
      if (!data || data.itemType === 'CHAT') return;
      removeCalledFromBoard(data.itemId);
    };

    const onItemRemoved = (data) => {
      // Item desatribuido do agente -> sai da visao "Minha"
      if (!data || data.itemtype !== KANBAN_ITEM_TYPE_CALLED) return;
      removeCalledFromBoard(data.itemid);
    };

    // Registra todos os callbacks; cada register* retorna unsubscribe.
    const unsubNew     = hubContext.registerNewItemCallback?.(onNewItem);
    const unsubUpdated = hubContext.registerItemUpdatedCallback?.(onItemUpdated);
    const unsubClosed  = hubContext.registerItemClosedCallback?.(onItemClosed);
    const unsubRemoved = hubContext.registerItemRemovedCallback?.(onItemRemoved);

    return () => {
      unsubNew?.();
      unsubUpdated?.();
      unsubClosed?.();
      unsubRemoved?.();
    };
  }, [hubContext, board?.id]);

  // ============================================================
  // Padrao B — Join/Leave dos grupos supervisor conforme viewMode (Opcao A: fan-out por org).
  //
  // Adm Sistema: 1 join sem orgId  -> grupo "_all" (ve TUDO do hirer)
  // Adm Org/Coord: N joins (1 por org do user) -> grupos "_org_{orgId}" especificos
  //
  // Estrategia robusta de reconnect (padrao senior):
  //   1) Refs sempre apontam pra config atual (targets, viewMode, hirerId).
  //   2) UM handler de reconnect e registrado durante toda a vida do componente
  //      (useEffect mount-only). Ele le tudo via ref → sempre o estado mais recente.
  //   3) "Alive sentinel" (aliveRef) protege contra zombie handlers: o cliente SignalR JS
  //      atual nao expoe `off()` pra onreconnected, mas o sentinel garante que apos unmount
  //      qualquer disparo do handler vira no-op imediato.
  // ============================================================

  // Refs que SEMPRE refletem o ultimo render. Permitem o handler de reconnect (registrado
  // uma unica vez) ler a configuracao atual sem precisar se re-registrar.
  const viewModeRef     = useRef(viewMode);
  const hirerIdRef      = useRef(hirerId);
  const targetsRef      = useRef([]);
  const aliveRef        = useRef(true);

  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);
  useEffect(() => { hirerIdRef.current = hirerId; }, [hirerId]);
  useEffect(() => {
    // targets atuais: Adm Sistema → [null], Adm Org/Coord → supervisorOrgIds
    targetsRef.current = isAdmSistema ? [null] : supervisorOrgIds.map((id) => id);
  }, [isAdmSistema, supervisorOrgIds]);

  // Helper compartilhado — sempre opera nos refs atuais (joinAll/leaveAll do React effect
  // chamam o mesmo codigo do handler de reconnect, sem duplicacao).
  const performJoinSupervisor = React.useCallback(async (conn) => {
    if (!aliveRef.current) return;
    if (!conn || !hirerIdRef.current) return;
    if (viewModeRef.current !== 'all') return;
    const targets = targetsRef.current;
    if (!targets || targets.length === 0) return;

    await Promise.all(targets.map(async (orgId) => {
      try {
        if (!aliveRef.current) return;
        await conn.invoke('JoinSupervisorGroup', hirerIdRef.current, orgId);
      } catch (_) { /* best-effort: gate denega ou hub reconectando */ }
    }));
  }, []);

  const performLeaveSupervisor = React.useCallback(async (conn, targetsSnapshot) => {
    if (!conn || !hirerIdRef.current) return;
    const targets = targetsSnapshot ?? targetsRef.current ?? [];
    if (targets.length === 0) return;

    await Promise.all(targets.map(async (orgId) => {
      try { await conn.invoke('LeaveSupervisorGroup', hirerIdRef.current, orgId); }
      catch (_) { /* idem */ }
    }));
  }, []);

  // (a) Mount-only: registra UM handler de onreconnected que vive enquanto o componente
  //     vive. Le tudo via ref. Guarded pelo aliveRef.
  useEffect(() => {
    const conn = hubContext?.agentWorkspaceHub;
    if (!conn || typeof conn.onreconnected !== 'function') return;

    aliveRef.current = true;
    const handler = async () => {
      if (!aliveRef.current) return;
      // No reconnect, os grupos SignalR somem do servidor; rejoinamos tudo.
      await performJoinSupervisor(conn);
    };
    conn.onreconnected(handler);

    return () => {
      // Sem off() na API atual do SignalR client JS — usamos sentinel pra neutralizar
      // chamadas futuras do handler. Eh idempotente e seguro.
      aliveRef.current = false;
    };
    // hubContext.agentWorkspaceHub muda quando a conexao e (re)criada do zero — nesse caso
    // re-registramos no nova conexao.
  }, [hubContext?.agentWorkspaceHub, performJoinSupervisor]);

  // (b) Conforme viewMode: join ao entrar em 'all', leave ao sair. Snapshot dos targets
  //     no cleanup pra garantir LeaveSupervisorGroup nos grupos certos mesmo se props
  //     mudaram entre join e leave.
  useEffect(() => {
    const conn = hubContext?.agentWorkspaceHub;
    if (!conn || !isSupervisor || viewMode !== 'all') return;

    aliveRef.current = true;
    performJoinSupervisor(conn);

    // Captura snapshot pro cleanup (orgIds podem mudar entre o join e o leave).
    const targetsSnapshot = targetsRef.current.slice();
    return () => { performLeaveSupervisor(conn, targetsSnapshot); };
  }, [viewMode, hubContext?.agentWorkspaceHub, isSupervisor, isAdmSistema, supervisorOrgIds, performJoinSupervisor, performLeaveSupervisor]);

  const applyOptimisticMove = (called, destColumn, targetStatus, extraData = {}) => {
    const originalColId = Object.entries(calledsByColumn)
      .find(([, items]) => items.some((c) => c.id === called.id))?.[0];

    optimisticMovesRef.current[String(called.id)] = {
      originalColId,
      originalCalled: { ...called },
      targetStatusId: targetStatus.statusId,
    };

    const updatedCalled = {
      ...called,
      statusId:       targetStatus.statusId,
      statusName:     targetStatus.statusName,
      statusColorHex: targetStatus.colorHex,
      ...extraData,
    };

    setCalledsByColumn((prev) => {
      const next = {};
      Object.entries(prev).forEach(([colId, items]) => {
        next[colId] = items.filter((c) => c.id !== called.id);
      });
      const destKey = String(destColumn.id);
      next[destKey] = [updatedCalled, ...(next[destKey] ?? [])];
      return next;
    });
  };

  const handleMoveError = (calledId, errorMessage) => {
    const info = optimisticMovesRef.current[String(calledId)];

    setCalledsByColumn((prev) => {
      const next = {};
      Object.entries(prev).forEach(([colId, items]) => {
        next[colId] = items.filter((c) => c.id !== calledId);
      });
      if (info?.originalColId) {
        next[info.originalColId] = [info.originalCalled, ...(next[info.originalColId] ?? [])];
      }
      return next;
    });

    delete optimisticMovesRef.current[String(calledId)];

    setToast({
      open: true,
      message: errorMessage || `Erro ao atualizar chamado #${calledId}`,
      severity: 'error',
    });
  };

  const handleFinishOptimisticClose = () => {
    if (!pendingMove) return;
    const { called, destColumn, targetStatus } = pendingMove;
    applyOptimisticMove(called, destColumn, targetStatus);
    setFinishModal({ open: false, calledId: null, targetStatusId: null, targetStatus: null });
    setPendingMove(null);
    setMovingCalled(false);
    setToast({ open: true, message: `Chamado #${called.id} movido com sucesso`, severity: 'success' });
  };

  const handleFillFieldsOptimisticClose = (extraCardData) => {
    if (!pendingMove) return;
    const { called, destColumn, targetStatus } = pendingMove;
    applyOptimisticMove(called, destColumn, targetStatus, extraCardData ?? {});
    setFillFieldsModal({ open: false, calledId: null, errorMessage: '' });
    setPendingMove(null);
    setMovingCalled(false);
    setToast({ open: true, message: `Chamado #${called.id} movido com sucesso`, severity: 'success' });
  };

  const handleFillFieldsBackgroundSuccess = async (calledId, extraCardData) => {
    const info = optimisticMovesRef.current[String(calledId)];
    if (!info?.targetStatusId) {
      delete optimisticMovesRef.current[String(calledId)];
      return;
    }
    try {
      const res = await moveCalledStatus(calledId, info.targetStatusId, info.originalCalled?.statusId);
      const resData = res?.data?.data ?? {};
      if (res?.data?.success && !resData.requiresdetailspage && !resData.requiresfinishmodal) {
        delete optimisticMovesRef.current[String(calledId)];
        if (extraCardData && Object.keys(extraCardData).length) {
          setCalledsByColumn((prev) => {
            const next = {};
            Object.entries(prev).forEach(([colId, items]) => {
              next[colId] = items.map((c) => c.id === calledId ? { ...c, ...extraCardData } : c);
            });
            return next;
          });
        }
      } else if (!res?.data?.success) {
        handleMoveError(calledId, res?.data?.errors?.[0] ?? 'Erro ao mover chamado');
      } else {
        handleMoveError(calledId, 'Não foi possível mover o chamado. Acesse o chamado e tente novamente.');
      }
    } catch {
      handleMoveError(calledId, 'Erro ao mover chamado. Tente novamente.');
    }
  };

  const handleLoadMore = async (droppableId) => {
    const colId = droppableId.split('__')[0];
    const meta = columnMeta[colId];
    if (!meta || meta.loadingMore) return;

    const nextPage = meta.page + 1;
    setColumnMeta((prev) => ({
      ...prev,
      [colId]: { ...prev[colId], loadingMore: true },
    }));

    try {
      const col = board.columns.find((c) => String(c.id) === colId);
      if (!col) {
        setColumnMeta((prev) => ({
          ...prev,
          [colId]: { ...prev[colId], loadingMore: false },
        }));
        return;
      }
      const { fields: ff, searches: fs }   = buildPaginateParams(board.filters);
      // Sem serverSearchTerm: load-more ocorre apenas no modo "board normal".
      // Quando o usuario esta buscando, searchResults sobrepoe calledsByColumn e este
      // codigo nao roda (UI bloqueia load-more nesse modo).
      // Padrao B: respeita viewMode tambem no load-more.
      const viewFf = [];
      const viewFs = [];
      if (!isClient && viewMode === 'mine' && currentUserId > 0) {
        viewFf.push('assigneduserid');
        viewFs.push(String(currentUserId));
      }
      const url = buildColumnUrl(col, [...ff, ...viewFf], [...fs, ...viewFs], nextPage);
      //const { fields: ff, searches: fs } = buildPaginateParams(board.filters);
      //const url = buildColumnUrl(col, ff, fs, nextPage);
      if (!url) return;
      const res      = await Api.get(url);
      const newItems = (res.data?.data ?? []).map(normalizeCalledItem);
      setCalledsByColumn((prev) => ({
        ...prev,
        [colId]: [...(prev[colId] ?? []), ...newItems],
      }));
      setColumnMeta((prev) => ({
        ...prev,
        [colId]: { page: nextPage, count: res.data?.count ?? meta.count, loadingMore: false },
      }));
    } catch {
      setColumnMeta((prev) => ({
        ...prev,
        [colId]: { ...prev[colId], loadingMore: false },
      }));
    }
  };

  const handleServerSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults(null);
      return;
    }
    if (!board) return;

    const { fields: ff, searches: fs } = buildPaginateParams(board.filters);
    const { fields: sf, searches: ss } = buildServerSearchParams(term);
    const allFields   = [...ff, ...sf];
    const allSearches = [...fs, ...ss];

    const fieldSearch = allFields.length > 0
      ? `&field=${allFields.join('|')}|&search=${allSearches.join('|')}|`
      : '';
    const baseUrl = `/calleds/paginate?page=1&size=10&type=0&isarchived=0&ofield=open&osearch=2${fieldSearch}`;

    // Mirror buildColumnUrl: group columns by their isfinished/iscanceled type
    const colTypes = new Set();
    board.columns.forEach((col) => {
      const typeIds = col.statuses.map((s) => s.statusTypeId);
      const isFinished = typeIds.some((t) => t === 5);
      const isCanceled = !isFinished && typeIds.some((t) => t === 4);
      if (isFinished) colTypes.add('finished');
      else if (isCanceled) colTypes.add('canceled');
      else colTypes.add('open');
    });

    const urls = [];
    if (colTypes.has('open'))     urls.push(`${baseUrl}&isfinished=0&iscanceled=0`);
    if (colTypes.has('finished')) urls.push(`${baseUrl}&isfinished=1&iscanceled=0`);
    if (colTypes.has('canceled')) urls.push(`${baseUrl}&isfinished=0&iscanceled=1`);
    if (urls.length === 0)        urls.push(`${baseUrl}&isfinished=0&iscanceled=0`);

    setSearchLoading(true);
    try {
      const responses = await Promise.all(urls.map((u) => Api.get(u)));
      const allItems  = responses.flatMap((res) => (res.data?.data ?? []).map(normalizeCalledItem));
      const seen = new Set();
      const unique = allItems.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      setSearchResults(unique);
    } catch (err) {
      setSearchResults([]);
      setToast({ open: true, message: 'Erro ao buscar chamados. Tente novamente.', severity: 'error' });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleToggleGroup = (colId, groupKey) => {
    const key = `${colId}__${groupKey}`;
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleDragStart = (initial) => {
    // split('__')[0] works for both "3" (grouped) and "3____all__" (non-grouped)
    const colId = initial.source.droppableId.split('__')[0];
    setIsDragging(true);
    setDraggingColId(colId);
    setInvalidFieldsCalledId(null);
  };

  const getColumnByDroppableId = (droppableId) => {
    const colId = parseInt(droppableId.split('__')[0], 10);
    return board?.columns.find((c) => c.id === colId) ?? null;
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);
    setDraggingColId(null);
    if (searchResults !== null) return;

    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Guard: same column by colId prefix
    const srcColId = source.droppableId.split('__')[0];
    const dstColId = destination.droppableId.split('__')[0];
    if (srcColId === dstColId) return;

    const allC        = Object.values(calledsByColumn).flat();
    const calledToMove = allC.find((c) => c.id === parseInt(draggableId, 10));
    if (!calledToMove) return;

    const destColumn = getColumnByDroppableId(destination.droppableId);
    if (!destColumn) return;

    if (isClient) {
      const srcTypeId = getCalledSourceStatusTypeId(calledToMove);
      const closedStatus = destColumn.statuses.find((s) => s.statusTypeId === 5);
      if (srcTypeId !== 2 || !closedStatus) {
        setToast({
          open: true,
          message: intl.formatMessage({ id: 'kanban.client.move.not.allowed', defaultMessage: 'Esta movimentação não é permitida para o seu perfil.' }),
          severity: 'error',
        });
        return;
      }
      if (destColumn.statuses.length > 1) {
        setPendingMove({ called: calledToMove, destColumn, destDroppableId: destination.droppableId });
        setStatusSelectOpen(true);
      } else {
        attemptClientFinish(calledToMove, closedStatus);
      }
      return;
    }

    if (destColumn.statuses.length > 1) {
      setPendingMove({ called: calledToMove, destColumn, destDroppableId: destination.droppableId });
      setStatusSelectOpen(true);
    } else {
      const targetStatus = destColumn.statuses[0];

      if (getCalledSourceStatusTypeId(calledToMove) === 1 && targetStatus.statusTypeId === 5) {
        setToast({
          open: true,
          message: 'Não é permitido mover um chamado com status "Novo" diretamente para um status de encerramento.',
          severity: 'error',
        });
        return;
      }

      setPendingMove({ called: calledToMove, destColumn, targetStatus, destDroppableId: destination.droppableId });
      setConfirmOpen(true);
    }
  };

  const handleStatusSelect = (status) => {
    setStatusSelectOpen(false);

    const srcTypeId = getCalledSourceStatusTypeId(pendingMove?.called);

    if (isClient) {
      if (srcTypeId !== 2 || status.statusTypeId !== 5) {
        setToast({
          open: true,
          message: intl.formatMessage({ id: 'kanban.client.move.not.allowed', defaultMessage: 'Esta movimentação não é permitida para o seu perfil.' }),
          severity: 'error',
        });
        setPendingMove(null);
        return;
      }
      attemptClientFinish(pendingMove.called, status);
      setPendingMove(null);
      return;
    }

    if (srcTypeId === 1 && status.statusTypeId === 5) {
      setToast({
        open: true,
        message: 'Não é permitido mover um chamado com status "Novo" diretamente para um status de encerramento.',
        severity: 'error',
      });
      setPendingMove(null);
      return;
    }

    setPendingMove((prev) => ({ ...prev, targetStatus: status }));
    setConfirmOpen(true);
  };

  const handleTreatCalledClosed = (calledId) => {
    setCalledsByColumn((prev) => {
      const next = {};
      Object.entries(prev).forEach(([colId, items]) => {
        next[colId] = items.filter((c) => c.id !== calledId);
      });
      return next;
    });
    setTreatModal({ open: false, calledId: null });
    setPendingMove(null);
    setToast({ open: true, message: `Chamado #${calledId} encerrado com sucesso`, severity: 'success' });
  };

  const attemptClientFinish = async (called, targetStatus) => {
    if (clientFinishInFlightRef.current.has(called.id)) return;
    clientFinishInFlightRef.current.add(called.id);
    setMovingCalled(true);
    try {
      const settingsRes = await getByOrganizationIdDapper(called.organizationId);
      if (!settingsRes?.success) {
        throw new Error(intl.formatMessage({ id: 'process.error' }));
      }
      if (!settingsRes?.data?.canclientfinishcalled) {
        setToast({
          open: true,
          message: intl.formatMessage({ id: 'kanban.client.finish.not.enabled', defaultMessage: 'Sua organização não permite o encerramento de chamados pelo cliente.' }),
          severity: 'error',
        });
        return;
      }

      const validationRes = await validateRequiredCustomFields(called.id);
      const vd = validationRes?.data?.data;
      if (vd && vd.isvalid === false) {
        setToast({
          open: true,
          message: vd.errormessage || intl.formatMessage({ id: 'kanban.client.finish.required.fields', defaultMessage: 'Campo personalizado obrigatório não preenchido.' }),
          severity: 'error',
        });
        return;
      }

      const cgRes = await getCalledGridById(called.id);
      const cg = cgRes?.data ?? cgRes ?? null;
      if (!cg?.id) throw new Error('Não foi possível carregar os dados do chamado');

      const formData = buildCalledFinishFormData({
        calledGridData: cg,
        calledId: called.id,
        targetStatusId: targetStatus.statusId,
        userId: userSession?.user?.id,
        description: intl.formatMessage({ id: 'kanban.client.finish.default.description', defaultMessage: 'Chamado encerrado pelo cliente via Kanban' }),
        privacy: false,
      });

      const res = await Api.put('/calleds', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.errors?.[0] ?? 'Erro ao finalizar chamado');
      }

      handleFinishSuccess(called.id, {
        statusId: targetStatus.statusId,
        statusName: targetStatus.statusName,
        colorHex: targetStatus.colorHex,
      });
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.[0] ?? err?.message ?? intl.formatMessage({ id: 'process.error' });
      setToast({
        open: true,
        message: errMsg,
        severity: 'error',
      });
    } finally {
      clientFinishInFlightRef.current.delete(called.id);
      setMovingCalled(false);
    }
  };

  const handleConfirmMove = async (confirmed, mode = 'default') => {
    setConfirmOpen(false);
    if (!confirmed || !pendingMove) { setPendingMove(null); return; }

    const { called, targetStatus, destColumn } = pendingMove;
    setMovingCalled(true);

    if (targetStatus.statusTypeId === 5 || targetStatus.statusTypeId === 9) {
      try {
        const validationRes = await validateRequiredCustomFields(called.id);
        const vd = validationRes?.data?.data;
        if (vd && vd.isvalid === false) {
          setToast({ open: true, message: vd.errormessage || 'Campo personalizado obrigatório não preenchido.', severity: 'error' });
          setInvalidFieldsCalledId(called.id);
          setPendingMove(null);
          setMovingCalled(false);
          return;
        }
      } catch {
        // falha silenciosa — prossegue com o fluxo normal
      }

      if (mode === 'quick') {
        dispatch(calledsActions.setTreatCalledModalData({
          id:                  called.id,
          title:               called.title,
          organizationid:      called.organizationId,
          Organization:        called.organizationName,
          OrganizationCategory: called.categoryName,
          requestingusername:  called.requestingUserName,
          status: {
            name:     called.statusName,
            colorhex: called.statusColorHex,
          },
          priority: called.priorityName ? {
            name:     called.priorityName,
            colorhex: called.priorityColorHex,
          } : null,
        }));
        setTreatModal({ open: true, calledId: called.id, targetStatusId: targetStatus.statusId });
        setMovingCalled(false);
        return;
      }
      setFinishModal({
        open: true,
        calledId: called.id,
        targetStatusId: targetStatus.statusId,
        targetStatus,
      });
      setMovingCalled(false);
      return;
    }

    // statusTypeId 1 = open status, exempt from required-field validation
    if (targetStatus.statusTypeId !== 1) {
      const missing = [];
      if (!called.assignedUserId)         missing.push('Responsável');
      if (!called.organizationCategoryId) missing.push('Categoria');
      if (missing.length > 0) {
        const fieldList = missing.length === 1
          ? missing[0]
          : missing.slice(0, -1).join(', ') + ' e ' + missing[missing.length - 1];
        setFillFieldsModal({
          open: true,
          calledId: called.id,
          errorMessage: `Para seguir com a mudança de status é necessário atualizar as seguintes informações: ${fieldList}`,
        });
        setMovingCalled(false);
        return;
      }
    }

    applyOptimisticMove(called, destColumn, targetStatus);

    const calledId   = called.id;
    const statusId   = targetStatus.statusId;
    const statusName = targetStatus.statusName;
    let timedOut = false;

    // basicdata must be saved before moveCalledStatus
    const bgPromise = (async () => {
      const cgRes = await getCalledGridById(calledId);
      const cg = cgRes?.data ?? cgRes ?? null;
      if (!cg?.id) throw new Error('Não foi possível carregar os dados do chamado');
      const fd = new FormData();
      fd.append('Id',                   calledId);
      fd.append('Title',                cg.title ?? '');
      fd.append('Description',          cg.description ?? ' ');
      fd.append('Type',                 cg.typeid ?? 0);
      fd.append('OrganizationCategory', cg.organizationcategoryid ?? 0);
      fd.append('AssignedUser',         cg.assigneduserid ?? 0);
      fd.append('TechinicalTeamId',     cg.techinicalteamid ?? 0);
      fd.append('AddressId',            cg.addressid ?? 0);
      fd.append('RequestingUserId',     cg.requestinguserid ?? 0);
      fd.append('UpdatedUser',          userSession?.user?.id ?? 0);
      if (cg.duedate) fd.append('DueDate', cg.duedate);
      const saveRes = await updateCalledBasicData(fd);
      if (!saveRes?.data?.success)
        throw new Error(saveRes?.data?.errors?.[0] ?? 'Erro ao salvar dados do chamado');
      const moveRes = await moveCalledStatus(calledId, statusId, called.statusId);
      const moveData = moveRes?.data?.data ?? {};
      if (!moveRes?.data?.success || moveData.requiresdetailspage || moveData.requiresfinishmodal)
        throw new Error(moveData.errormessage ?? moveRes?.data?.errors?.[0] ?? 'Não foi possível mover o chamado');
      return moveRes;
    })();

    timerRef.current = setTimeout(() => {
      timedOut = true;
      setMovingCalled(false);
      setPendingMove(null);
      setToast({ open: true, message: `Chamado #${calledId} movido para "${statusName}"`, severity: 'success' });
      bgPromise
        .then(() => { delete optimisticMovesRef.current[String(calledId)]; })
        .catch((err) => handleMoveError(calledId, err?.message || 'Erro ao mover chamado'));
    }, 5000);

    bgPromise
      .then(() => {
        clearTimeout(timerRef.current);
        if (timedOut) return;
        setMovingCalled(false);
        setPendingMove(null);
        delete optimisticMovesRef.current[String(calledId)];
        setToast({ open: true, message: `Chamado #${calledId} movido para "${statusName}"`, severity: 'success' });
      })
      .catch((err) => {
        clearTimeout(timerRef.current);
        if (timedOut) return;
        setMovingCalled(false);
        setPendingMove(null);
        handleMoveError(calledId, err?.message || 'Erro ao mover chamado');
      });
  };

  const handleFinishSuccess = (calledId, newStatusData) => {
    setCalledsByColumn((prev) => {
      const calledToUpdate = Object.values(prev).flat().find((c) => c.id === calledId);
      const next = {};
      Object.entries(prev).forEach(([colId, items]) => {
        next[colId] = items.filter((c) => c.id !== calledId);
      });
      if (calledToUpdate) {
        const destCol = board.columns.find((col) =>
          col.statuses.some((s) => s.statusId === newStatusData.statusId)
        );
        if (destCol) {
          const destKey = String(destCol.id);
          next[destKey] = [
            { ...calledToUpdate, statusId: newStatusData.statusId, statusName: newStatusData.statusName, statusColorHex: newStatusData.colorHex },
            ...(next[destKey] ?? []),
          ];
        }
      }
      return next;
    });
    setFinishModal({ open: false, calledId: null, targetStatusId: null, targetStatus: null });
    setPendingMove(null);
    setToast({ open: true, message: `Chamado #${calledId} encerrado com sucesso`, severity: 'success' });
  };

  const getCalledSourceStatusTypeId = (called) => {
    if (!board || !called) return null;
    for (const col of board.columns) {
      for (const st of col.statuses) {
        if (st.statusId === called.statusId) return st.statusTypeId;
      }
    }
    return null;
  };

  const handleCancelStatusSelect = () => {
    setStatusSelectOpen(false);
    setPendingMove(null);
  };

  if (loading && !board) {
    return <KanbanSkeleton />;
  }

  if (error || !board) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="error">{error ?? 'Quadro não encontrado.'}</Typography>
      </Box>
    );
  }

  const isGrouped = board.groupBy !== 'NONE';

  const allCalleds = searchResults !== null
    ? searchResults
    : Object.values(calledsByColumn).flat();

  const displayCalledsByColumn = (() => {
    if (searchResults !== null) {
      // Modo busca: distribui searchResults nas colunas pelo statusId
      const result = {};
      (board?.columns ?? []).forEach((col) => {
        const colStatusIds = new Set(col.statuses.map((s) => s.statusId));
        result[String(col.id)] = searchResults.filter((c) => colStatusIds.has(c.statusId));
      });
      return result;
    }
    // Modo normal: filtro local por texto
    if (!searchTerm) return calledsByColumn;
    const q = searchTerm.toLowerCase();
    const result = {};
    Object.entries(calledsByColumn).forEach(([colId, items]) => {
      result[colId] = items.filter((c) =>
        c.title?.toLowerCase().includes(q) ||
        String(c.id).includes(searchTerm) ||
        c.organizationName?.toLowerCase().includes(q)
      );
    });
    return result;
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <KanbanIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Tooltip title={board.title} placement="bottom">
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }} noWrap>
                  {board.title}
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Organize e acompanhe seus chamados em colunas personalizáveis
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Buscar tickets..."
              variant="outlined"
              size="small"
              disabled={loading}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!e.target.value.trim()) setSearchResults(null);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleServerSearch(); }}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Pesquisar">
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={handleServerSearch}
                        sx={{ color: searchResults !== null ? 'primary.main' : 'text.disabled', p: '2px' }}
                      >
                        <SearchIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => history.push(`/${lang}/calendar`)}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                borderColor: '#d1d5db',
                color: 'text.secondary',
                flexShrink: 0,
                '&:hover': { borderColor: '#9ca3af', bgcolor: 'action.hover' },
              }}
            >
              Ver Calendário
            </Button>

            <Chip
              label={`${allCalleds.length} chamados`}
              size="small"
              sx={{ bgcolor: '#f3f4f6', color: 'text.secondary', fontSize: '0.7rem', flexShrink: 0 }}
            />

            {onToggleSidebar && (
              <Tooltip title={sidebarOpen ? 'Recolher quadros' : 'Expandir quadros'}>
                <IconButton
                  size="small"
                  onClick={onToggleSidebar}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '8px', '&:hover': { bgcolor: 'action.hover' }, width: 36, height: 36 }}
                >
                  {sidebarOpen ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Padrao B: toggle visivel apenas pra supervisor/admin global. */}
        {isSupervisor && (
          <Tabs
            value={viewMode}
            onChange={(_e, v) => { if (!loading) setViewMode(v); }}
            sx={{ px: 2, minHeight: 40, borderTop: '1px solid', borderColor: 'divider', '& .MuiTabs-indicator': { backgroundColor: 'primary.main' } }}
          >
            <Tab value="mine" label={intl.formatMessage({ id: 'kanban.view.mine' })} disabled={loading} sx={{ textTransform: 'none', minHeight: 40, '&.Mui-selected': { color: 'primary.main' } }} />
            <Tab value="all"  label={intl.formatMessage({ id: 'kanban.view.all' })}  disabled={loading} sx={{ textTransform: 'none', minHeight: 40, '&.Mui-selected': { color: 'primary.main' } }} />
          </Tabs>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', p: 2 }}>
        {loading ? (
          <Grid container spacing={1.5} sx={{ height: isGrouped ? 'auto' : `calc(100vh - ${isSupervisor ? 260 : 220}px)` }}>
            {board.columns.map((col) => (
              <Grid item key={col.id} xs={12} md={Math.max(2, Math.floor(12 / board.columns.length))} sx={{ height: isGrouped ? 'auto' : '100%' }}>
                <KanbanColumnSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <Grid container spacing={1.5} sx={{ height: isGrouped ? 'auto' : `calc(100vh - ${isSupervisor ? 260 : 220}px)` }}>
            {(() => {
              // Master group list uses unfiltered calleds so groups stay visible when local search hides all their tickets
              const cfg = GROUP_LABELS[board.groupBy];
              const masterGroups = isGrouped
                ? groupCalleds(Object.values(calledsByColumn).flat(), board.groupBy)
                : undefined;

              return board.columns.map((col) => {
              const droppableId   = isGrouped ? String(col.id) : `${col.id}____all__`;
              const colMeta       = columnMeta[String(col.id)] ?? { page: 1, count: 0, loadingMore: false };
              const colCalleds    = displayCalledsByColumn[String(col.id)] ?? [];
              const rawColCalleds = calledsByColumn[String(col.id)] ?? [];
              const hasMoreFromServer = searchResults === null && rawColCalleds.length < colMeta.count;
              const columnTotalCount  = searchResults !== null ? colCalleds.length : colMeta.count;
              // Project master groups onto each column to preserve order and show 0-ticket groups
              const groups = isGrouped
                ? masterGroups.map((mg) => ({
                    ...mg,
                    calleds: cfg
                      ? colCalleds.filter((c) => {
                          const k = c[cfg.key] || '(Sem ' + cfg.label.toLowerCase() + ')';
                          return k === mg.key;
                        })
                      : colCalleds,
                  }))
                : undefined;
              return (
                <Grid
                  item
                  key={col.id}
                  xs={12}
                  md={Math.max(2, Math.floor(12 / board.columns.length))}
                  sx={{ height: isGrouped ? 'auto' : '100%' }}
                >
                  <KanbanColumn
                    column={col}
                    calleds={colCalleds}
                    groups={groups}
                    collapsedGroups={collapsedGroups}
                    onToggleGroup={handleToggleGroup}
                    isDragging={isDragging}
                    isSourceColumn={isDragging && draggingColId === String(col.id)}
                    invalidFieldsCalledId={invalidFieldsCalledId}
                    onCardClick={(c) => { setSelectedCalled(c); setModalOpen(true); }}
                    hasMoreFromServer={hasMoreFromServer}
                    loadingMore={colMeta.loadingMore}
                    totalCount={columnTotalCount}
                    onLoadMore={handleLoadMore}
                    droppableId={droppableId}
                  />
                </Grid>
              );
            });
          })()}
          </Grid>
        </DragDropContext>
        )}
      </Box>

      <TicketDetailModal
        ticket={selectedCalled ? {
          ...selectedCalled,
          status:   selectedCalled.statusName,
          priority: selectedCalled.priorityName,
          client:   selectedCalled.organizationName,
          assignedTo: selectedCalled.assignedUserName,
          category: selectedCalled.categoryName,
          predictedDate: selectedCalled.dueDate,
        } : null}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedCalled(null); }}
        getStatusConfig={(s) => ({ bgColor: '#f3f4f6', color: '#6b7280', icon: null })}
        lang={lang}
      />

      <StatusSelectDialog
        open={statusSelectOpen}
        column={pendingMove?.destColumn}
        onSelect={handleStatusSelect}
        onCancel={handleCancelStatusSelect}
      />

      {pendingMove?.targetStatus && (
        <ConfirmationDialog
          open={confirmOpen}
          onClose={handleConfirmMove}
          onConfirm={handleConfirmMove}
          fromStatus={pendingMove.called?.statusName}
          toStatus={pendingMove.targetStatus?.statusName}
          targetStatusTypeId={pendingMove.targetStatus?.statusTypeId}
        />
      )}

      <TreatCalledModal
        open={treatModal.open}
        onClose={() => {
          setTreatModal({ open: false, calledId: null, targetStatusId: null });
          setPendingMove(null);
        }}
        onAssignCalled={() => {}}
        onShowNotification={(variant, message) =>
          setToast({ open: true, message, severity: variant === 'success' ? 'success' : 'error' })
        }
        onCalledClosed={handleTreatCalledClosed}
        targetStatusId={treatModal.targetStatusId}
        skipAssignment
      />

      <KanbanFinishModal
        open={finishModal.open}
        calledId={finishModal.calledId}
        targetStatusId={finishModal.targetStatusId}
        targetStatus={finishModal.targetStatus}
        onClose={() => {
          setFinishModal({ open: false, calledId: null, targetStatusId: null, targetStatus: null });
          setPendingMove(null);
        }}
        onFinishSuccess={handleFinishSuccess}
        onOptimisticClose={handleFinishOptimisticClose}
        onMoveError={handleMoveError}
      />
      
      <CustomizedSnackbars
        variant={toast.severity}
        message={toast.message}
        isOpen={toast.open}
        toClose={() => setToast((t) => ({ ...t, open: false }))}
      />
      

      <KanbanFillFieldsModal
        open={fillFieldsModal.open}
        calledId={fillFieldsModal.calledId}
        called={pendingMove?.called}
        errorMessage={fillFieldsModal.errorMessage}
        lang={lang}
        onClose={() => {
          setFillFieldsModal({ open: false, calledId: null, errorMessage: '' });
          setPendingMove(null);
        }}
        onSaveSuccess={(extraCardData) => {
          if (!pendingMove) {
            setFillFieldsModal({ open: false, calledId: null, errorMessage: '' });
            return;
          }
          const { called, targetStatus, destColumn } = pendingMove;

          // Fecha o modal e aplica o move otimista com os dados atualizados (inclui campos preenchidos)
          setFillFieldsModal({ open: false, calledId: null, errorMessage: '' });
          const updatedCalled = {
            ...called,
            statusId:               targetStatus.statusId,
            statusName:             targetStatus.statusName,
            statusColorHex:         targetStatus.colorHex,
            assignedUserId:         extraCardData?.assignedUserId  ?? called.assignedUserId,
            organizationCategoryId: extraCardData?.organizationCategoryId ?? called.organizationCategoryId,
            ...(extraCardData ?? {}),
          };
          setCalledsByColumn((prev) => {
            const next = {};
            Object.entries(prev).forEach(([colId, items]) => {
              next[colId] = items.filter((c) => c.id !== called.id);
            });
            const destKey = String(destColumn.id);
            next[destKey] = [updatedCalled, ...(next[destKey] ?? [])];
            return next;
          });
          setToast({ open: true, message: `Chamado #${called.id} movido com sucesso`, severity: 'success' });
          setPendingMove(null);
          setMovingCalled(false);

          // Atualiza status no cache/histórico em background (FillFields já salvou o basicData)
          moveCalledStatus(called.id, targetStatus.statusId, called.statusId).catch((err) => {
            handleMoveError(called.id, err?.response?.data?.errors?.[0] ?? 'Erro ao atualizar status');
          });
        }}
        onOptimisticClose={handleFillFieldsOptimisticClose}
        onMoveError={handleMoveError}
        onBackgroundSuccess={(calledId, extra) => handleFillFieldsBackgroundSuccess(calledId, extra)}
      />

      {(movingCalled || searchLoading) && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            bgcolor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: '#fff' }} size={48} />
        </Box>
      )}
    </Box>
  );
};

export default TicketKanban;
